import { useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ConfirmDialog } from '../components/confirm-dialog';
import {
  Block,
  CoachButton,
  EmptyCircle,
  ErrorNote,
  Eyebrow,
  Loading,
  Row,
  SageCheck,
  Screen,
} from '../components/primitives';
import { ProLock } from '../components/pro-lock';
import { coaching, type ProgramDayRow } from '../lib/api';
import { longDay, shortDay } from '../lib/format';
import { isProRequired } from '../lib/pro';
import { useFeature, useProgram, usePrograms, useRefreshCoaching } from '../lib/queries';

const dayMeta = (d: ProgramDayRow) => {
  switch (d.state) {
    case 'done':
      return 'Done';
    case 'today':
      return 'Today';
    case 'available':
      return 'Open';
    default:
      return d.unlocksOn ? `Opens ${shortDay(d.unlocksOn)}` : `Day ${d.dayNo}`;
  }
};

/** One program: what it is, its days as they stand today, and the way in or out. */
export function ProgramPage() {
  const { idOrKey } = useParams();
  const navigate = useNavigate();
  const state = useFeature('programs');
  const program = useProgram(idOrKey!);
  const programs = usePrograms(state === 'on');
  const refresh = useRefreshCoaching();
  const [confirmLeave, setConfirmLeave] = useState(false);

  const enroll = useMutation({
    mutationFn: () => coaching.enrollProgram(program.data!.id),
    meta: { silentErrorCodes: ['coaching_pro_required'] },
    onSuccess: (p) => {
      coaching.event('program_started', { program: p.id });
      void refresh();
      navigate(`/programs/${p.id}/days/1`);
    },
  });
  const leave = useMutation({
    mutationFn: () => coaching.leaveProgram(program.data!.id),
    meta: { successMessage: 'You have left the program.' },
    onSuccess: (p) => {
      coaching.event('program_left', { program: p.id });
      setConfirmLeave(false);
      void refresh();
    },
  });

  if (state === 'off') return <Navigate to="/achieve" replace />;
  if (state === 'locked') {
    return (
      <Screen back="/programs" eyebrow="Program" title="Guided programs">
        <ProLock feature="programs" />
      </Screen>
    );
  }
  if (program.isPending) return <Loading />;
  if (program.isError)
    return <ErrorNote error={program.error} retry={() => void program.refetch()} />;

  const p = program.data;
  const e = p.enrollment;
  const active = e?.status === 'active';
  const otherActive =
    programs.data?.find((x) => x.enrollment?.status === 'active' && x.id !== p.id) ?? null;

  return (
    <Screen
      back="/programs"
      eyebrow={active && e ? `Day ${e.currentDay} of ${p.daysCount}` : `${p.daysCount} days`}
      title={p.title}
      subtitle={p.tagline}
    >
      {p.description && <Block>{p.description}</Block>}
      {!active && (
        <Block
          eyebrow={e?.status === 'completed' ? 'Completed' : 'Start'}
          title={
            e?.status === 'completed' && e.completedOn
              ? `You finished this on ${longDay(e.completedOn)}.`
              : 'One day at a time.'
          }
          action={
            p.locked ? (
              <ProLock feature="programs" compact />
            ) : otherActive ? (
              <p className="text-sm leading-relaxed text-ink/70">
                Finish or leave{' '}
                <Link to={`/programs/${otherActive.id}`} className="underline underline-offset-4">
                  {otherActive.title}
                </Link>{' '}
                first.
              </p>
            ) : (
              <CoachButton kind="gold" disabled={enroll.isPending} onClick={() => enroll.mutate()}>
                {e ? 'Start again' : 'Start this program'}
              </CoachButton>
            )
          }
        >
          {e?.status === 'completed'
            ? 'You can start it again from day 1.'
            : 'Day 1 opens as soon as you start. If today already has a plan, your first action arrives tomorrow morning.'}
        </Block>
      )}
      {enroll.isError && isProRequired(enroll.error) && <ProLock feature="programs" />}
      <Eyebrow className="border-b border-border px-5 pb-3 pt-6">Days</Eyebrow>
      {p.days.map((d) => {
        const open = active && d.state !== 'locked';
        return (
          <Row
            key={d.dayNo}
            mark={d.state === 'done' ? <SageCheck size={18} /> : <EmptyCircle />}
            title={d.title}
            meta={active ? dayMeta(d) : `Day ${d.dayNo}`}
            to={open ? `/programs/${p.id}/days/${d.dayNo}` : undefined}
          />
        );
      })}
      {active && (
        <div className="px-5 py-6">
          <CoachButton kind="quiet" onClick={() => setConfirmLeave(true)}>
            Leave this program
          </CoachButton>
        </div>
      )}
      <ConfirmDialog
        open={confirmLeave}
        title="Leave this program?"
        body="Your progress stays. You can start it again from day 1 later."
        confirmLabel="Leave"
        busy={leave.isPending}
        onConfirm={() => leave.mutate()}
        onCancel={() => setConfirmLeave(false)}
      />
    </Screen>
  );
}
